<template>
    <Modal design="v3" :show-footer="false" :show-title="false" @cancel="cancel" class-name="create-invoice-modal-v3">
        <div class="invoice-modal-content">
            <!-- Header -->
            <div class="modal-header">
                <div class="header-icon">
                    <i class="material-icons">receipt_long</i>
                </div>
                <h2 class="header-title">Yeni Fatura Oluştur</h2>
                <p class="header-subtitle">Fatura bilgilerini doldurun</p>
            </div>

            <!-- Body -->
            <div class="modal-body">
                <div class="form-container">
                    <!-- Fatura Tipi -->
                    <div class="form-field">
                        <label class="field-label">Fatura Tipi</label>
                        <a-select v-model="type" placeholder="Fatura tipi seçin" size="large">
                            <a-select-option v-for="(option, index) in invoiceTypes" :key="index" :value="option">
                                {{ formatBillType(option) }}
                            </a-select-option>
                        </a-select>
                    </div>

                    <!-- Tutar -->
                    <div class="form-field">
                        <div class="price-label-row">
                            <label class="field-label">Tutar (₺)</label>
                            <a-switch
                                v-if="!isWalletDeposit"
                                v-model="priceTypeSwitch"
                                checked-children="KDV Dahil"
                                un-checked-children="KDV Hariç" />
                        </div>
                        <a-input-number
                            v-model="price"
                            placeholder="0.00"
                            size="large"
                            :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                            :parser="value => value.replace(/\$\s?|(,*)/g, '')" />
                    </div>

                    <!-- Tarihler -->
                    <div class="date-fields-group">
                        <div class="form-field">
                            <label class="field-label">Oluşturulma Tarihi</label>
                            <a-date-picker
                                v-model="createdDate"
                                format="DD/MM/YYYY"
                                placeholder="Tarih seçin"
                                size="large"
                                dropdownClassName="invoice-date-picker" />
                        </div>
                        <div class="form-field">
                            <label class="field-label">Son Ödeme Tarihi</label>
                            <a-date-picker
                                v-model="dueDate"
                                format="DD/MM/YYYY"
                                placeholder="Tarih seçin"
                                size="large"
                                dropdownClassName="invoice-date-picker" />
                        </div>
                    </div>

                    <!-- Ödeme Durumu -->
                    <div class="form-field">
                        <a-checkbox v-model="paid">
                            <span>Ödendi olarak işaretle</span>
                        </a-checkbox>
                    </div>

                    <!-- Ödeme Tarihi -->
                    <div class="form-field" v-if="paid || paymentDate">
                        <label class="field-label">Ödeme Tarihi</label>
                        <a-date-picker
                            v-model="paymentDate"
                            format="DD/MM/YYYY"
                            placeholder="Tarih seçin"
                            size="large"
                            dropdownClassName="invoice-date-picker" />
                    </div>

                    <!-- Açıklama -->
                    <div class="form-field">
                        <label class="field-label">Açıklama</label>
                        <a-textarea
                            v-model="description"
                            placeholder="Fatura açıklaması giriniz..."
                            :rows="4"
                            :auto-size="{ minRows: 4, maxRows: 6 }" />
                    </div>
                </div>

                <!-- Fiyat Özeti -->
                <div class="price-summary" v-if="price > 0">
                    <div class="summary-line" v-if="!isWalletDeposit">
                        <span>KDV Hariç:</span>
                        <span>{{ formatPrice(priceWithoutVat) }} ₺</span>
                    </div>
                    <div class="summary-line" v-if="!isWalletDeposit">
                        <span>KDV (%{{ effectiveVatRate }}):</span>
                        <span>{{ formatPrice(vatAmount) }} ₺</span>
                    </div>
                    <div class="summary-line" v-if="isWalletDeposit">
                        <span>KDV:</span>
                        <span>0.00 ₺</span>
                    </div>
                    <div class="summary-line summary-total">
                        <span>Toplam:</span>
                        <span class="total-price">{{ formatPrice(isWalletDeposit ? price : priceWithVat) }} ₺</span>
                    </div>
                    <div class="summary-note" v-if="isWalletDeposit">
                        <span>KDV uygulanmaz</span>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="modal-footer">
                <SupsisButton ghost @click="cancel">İptal</SupsisButton>
                <SupsisButton primary @click="create">
                    <i class="material-icons">add</i>
                    <span>Fatura Oluştur</span>
                </SupsisButton>
            </div>
        </div>
    </Modal>
</template>

<script>
import Modal from "../common/Modal2.vue";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";
import { mapState } from "vuex";

export default {
    name: "CreateInvoiceModal",
    components: {
        Modal,
        SupsisButton,
    },
    data() {
        return {
            createdDate: this.$moment(),
            dueDate: this.$moment().add(15, 'days'),
            paymentDate: null,
            price: 0,
            description: null,
            paid: false,
            type: "manuel",
            priceType: "withVat",
            invoiceTypes: [
                "manuel",
                "monthly-3",
                "monthly-6",
                "annual",
                "annual-2",
                "education",
                "consultancy",
                "installation",
                "quick-purchase",
                "wallet-deposit",
                "other",
            ],
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
        isWalletDeposit() {
            return this.type === "wallet-deposit";
        },
        effectiveVatRate() {
            if (this.isWalletDeposit) {
                return 0;
            }
            return this.site?.vatRate || 20;
        },
        vatRateDecimal() {
            return this.effectiveVatRate / 100;
        },
        priceWithoutVat() {
            if (this.priceType === "withVat" && !this.isWalletDeposit) {
                return this.price / (1 + this.vatRateDecimal);
            }
            return this.price;
        },
        vatAmount() {
            if (this.isWalletDeposit) {
                return 0;
            }
            if (this.priceType === "withVat") {
                return this.price - this.priceWithoutVat;
            } else {
                return this.price * this.vatRateDecimal;
            }
        },
        priceWithVat() {
            if (this.isWalletDeposit) {
                return this.price;
            }
            if (this.priceType === "withVat") {
                return this.price;
            } else {
                return this.price + this.vatAmount;
            }
        },
        priceTypeSwitch: {
            get() {
                return this.priceType === "withVat";
            },
            set(value) {
                this.priceType = value ? "withVat" : "withoutVat";
            },
        },
    },
    watch: {
        paid(newVal) {
            if (newVal && !this.paymentDate) {
                this.paymentDate = this.$moment();
            } else if (!newVal) {
                this.paymentDate = null;
            }
        },
        paymentDate(newVal) {
            if (newVal && !this.paid) {
                this.paid = true;
            }
        },
        type(newVal) {
            if (newVal === "wallet-deposit") {
                this.priceType = "withVat";
            }
        },
    },
    methods: {
        cancel() {
            this.$emit("cancel");
        },
        create() {
            const paymentDate = this.paid ? this.paymentDate : null;
            let finalPrice;
            if (this.isWalletDeposit) {
                finalPrice = this.price;
            } else {
                finalPrice = this.priceType === "withVat" ? this.price : this.priceWithVat;
            }
            this.$emit("create", finalPrice, this.description, this.createdDate, this.dueDate, this.paid, this.type, paymentDate);
        },
        formatPrice(price) {
            return parseFloat(price || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        formatBillType(type) {
            const types = {
                manuel: "Manuel",
                monthly: "Aylık",
                "monthly-3": "3 Aylık",
                "monthly-6": "6 Aylık",
                annual: "Yıllık",
                "annual-2": "2 Yıllık",
                education: "Eğitim",
                consultancy: "Danışmanlık",
                installation: "Kurulum",
                other: "Diğer",
                "quick-purchase": "Paket Satın Alma",
                "wallet-deposit": "Bakiye Yükleme",
            };
            return types[type] || type;
        },
    },
};
</script>

<style lang="scss">
.invoice-date-picker {
    z-index: 999999999999999;
}

.ant-modal-root.v3.create-invoice-modal-v3 {
    .ant-modal {
        max-width: 600px;
        width: 90vw !important;
    }

    .ant-modal-content {
        padding: 0;
    }
}

.invoice-modal-content {
    display: flex;
    flex-direction: column;
    background: #ffffff;

    .modal-header {
        padding: 32px 32px 24px;
        text-align: center;
        border-bottom: 1px solid #f0f0f0;
        display: flex;
        flex-direction: column;
        align-items: center;

        .header-icon {
            width: 72px;
            height: 72px;
            margin-bottom: 20px;
            background: linear-gradient(135deg, #ec4b1d 0%, #f8734d 100%);
            border-radius: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 8px 24px rgba(236, 75, 29, 0.25);
            flex-shrink: 0;

            .material-icons {
                font-size: 36px;
                color: white;
            }
        }

        .header-title {
            font-size: 26px;
            font-weight: 700;
            color: #1e293b;
            margin: 0 0 8px;
            display: block;
            width: 100%;
        }

        .header-subtitle {
            font-size: 15px;
            color: #64748b;
            margin: 0;
            display: block;
            width: 100%;
        }
    }

    .modal-body {
        padding: 24px 32px;
        max-height: 65vh;
        overflow-y: auto;

        .form-container {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }

        .form-field {
            display: flex;
            flex-direction: column;

            .field-label {
                font-size: 14px;
                font-weight: 600;
                color: #334155;
                margin-bottom: 8px;
            }

            .price-label-row {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 8px;
            }

            .ant-input-number,
            .ant-select,
            .ant-picker,
            .ant-input {
                width: 100%;
            }
        }

        .date-fields-group {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
        }

        .price-summary {
            margin-top: 24px;
            padding: 20px;
            background: #f8fafc;
            border-radius: 12px;
            border: 1px solid #e2e8f0;

            .summary-line {
                display: flex;
                justify-content: space-between;
                padding: 8px 0;
                font-size: 14px;
                color: #64748b;

                &.summary-total {
                    margin-top: 12px;
                    padding-top: 12px;
                    border-top: 2px solid #e2e8f0;
                    font-weight: 600;
                    color: #1e293b;

                    .total-price {
                        font-size: 20px;
                        color: #ec4b1d;
                        font-weight: 700;
                    }
                }
            }

            .summary-note {
                margin-top: 8px;
                padding-top: 8px;
                border-top: 1px solid #e2e8f0;
                text-align: center;
                font-size: 12px;
                color: #94a3b8;
                font-style: italic;
            }
        }
    }

    .modal-footer {
        padding: 20px 32px;
        border-top: 1px solid #f0f0f0;
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        background: #fafbfc;

        .supsis-button {
            min-width: 140px;
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;

            .material-icons {
                font-size: 18px;
            }
        }
    }
}

@media (max-width: 768px) {
    .invoice-modal-content {
        .modal-body {
            padding: 20px 24px;

            .date-fields-group {
                grid-template-columns: 1fr;
            }
        }

        .modal-header {
            padding: 24px 24px 20px;
        }

        .modal-footer {
            padding: 16px 24px;
            flex-direction: column;

            .supsis-button {
                width: 100%;
            }
        }
    }
}
</style>
